import React, { useState } from 'react'
import { Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'

export default function ContactForm() {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    besked: '',
    website_url: '', // Honeypot field
  })

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  })

  const [errors, setErrors] = useState({})

  const validate = () => {
    const newErrors = {}
    if (!formData.navn.trim()) {
      newErrors.navn = 'Navn er påkrævet'
    }
    if (!formData.telefon.trim()) {
      newErrors.telefon = 'Telefonnummer er påkrævet'
    } else if (!/^[0-9+ ]{8,15}$/.test(formData.telefon.replace(/\s/g, ''))) {
      newErrors.telefon = 'Ugyldigt telefonnummer'
    }
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ugyldig e-mailadresse'
    }
    if (!formData.besked.trim()) {
      newErrors.besked = 'Besked er påkrævet'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setStatus({ submitting: true, success: false, error: null })

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null })
        setFormData({
          navn: '',
          telefon: '',
          email: '',
          besked: '',
          website_url: '',
        })
      } else {
        throw new Error('Der skete en fejl ved afsendelse. Prøv igen eller ring til os.')
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der skete en fejl. Prøv venligst igen.',
      })
    }
  }

  return (
    <div className="bg-[#1e1e1e] border border-white/10 rounded-sm p-6 sm:p-10 shadow-xl text-white">
      <h3 className="text-2xl font-bold text-white mb-2 tracking-tight">Få et uforpligtende tilbud</h3>
      <p className="text-stone-300 text-sm mb-8 leading-relaxed">
        Udfyld formularen, så vender vi tilbage til dig hurtigst muligt med et skræddersyet tilbud på dit projekt.
      </p>

      {status.success ? (
        <div className="bg-white/5 border border-white/10 rounded-sm p-6 text-center animate-in fade-in duration-300">
          <CheckCircle2 className="h-12 w-12 text-[#c08f66] mx-auto mb-4" />
          <h4 className="text-lg font-bold text-white mb-2">Tak for din henvendelse!</h4>
          <p className="text-stone-300 text-sm leading-relaxed">
            Vi har nu modtaget din besked og ser frem til at hjælpe dig. Vi bestræber os altid på at besvare dit spørgsmål hurtigst muligt.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute -top-96 -left-96" aria-hidden="true">
            <label htmlFor="website_url">Lad dette felt være tomt</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Navn */}
          <div>
            <label htmlFor="navn" className="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-1">
              Navn <span className="text-[#c08f66]">*</span>
            </label>
            <input
              type="text"
              id="navn"
              name="navn"
              value={formData.navn}
              onChange={handleChange}
              className={`bg-transparent border-b text-white focus:border-[#c08f66] placeholder:text-white/40 focus:outline-none py-3 px-1 w-full text-sm transition-colors ${
                errors.navn ? 'border-red-400 focus:border-red-400' : 'border-white/20'
              }`}
              placeholder="Dit fulde navn"
            />
            {errors.navn && <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {errors.navn}</p>}
          </div>

          {/* Telefon */}
          <div>
            <label htmlFor="telefon" className="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-1">
              Telefonnummer <span className="text-[#c08f66]">*</span>
            </label>
            <input
              type="tel"
              id="telefon"
              name="telefon"
              value={formData.telefon}
              onChange={handleChange}
              className={`bg-transparent border-b text-white focus:border-[#c08f66] placeholder:text-white/40 focus:outline-none py-3 px-1 w-full text-sm transition-colors ${
                errors.telefon ? 'border-red-400 focus:border-red-400' : 'border-white/20'
              }`}
              placeholder="F.eks. 20 53 27 67"
            />
            {errors.telefon && <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {errors.telefon}</p>}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-1">
              E-mailadresse
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`bg-transparent border-b text-white focus:border-[#c08f66] placeholder:text-white/40 focus:outline-none py-3 px-1 w-full text-sm transition-colors ${
                errors.email ? 'border-red-400 focus:border-red-400' : 'border-white/20'
              }`}
              placeholder="din.email@eksempel.dk"
            />
            {errors.email && <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {errors.email}</p>}
          </div>

          {/* Beskrivelse */}
          <div>
            <label htmlFor="besked" className="block text-xs font-semibold uppercase tracking-wider text-stone-300 mb-1">
              Beskrivelse af opgaven <span className="text-[#c08f66]">*</span>
            </label>
            <textarea
              id="besked"
              name="besked"
              rows="3"
              value={formData.besked}
              onChange={handleChange}
              className={`bg-transparent border-b text-white focus:border-[#c08f66] placeholder:text-white/40 focus:outline-none py-3 px-1 w-full text-sm transition-colors resize-none ${
                errors.besked ? 'border-red-400 focus:border-red-400' : 'border-white/20'
              }`}
              placeholder="Beskriv dit projekt så detaljeret som muligt..."
            />
            {errors.besked && <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {errors.besked}</p>}
          </div>

          {status.error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-sm text-red-200 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
              <span>{status.error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status.submitting}
            className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#c08f66] hover:bg-[#a6764e] text-white font-semibold px-8 py-3.5 rounded-sm shadow-md transition-colors duration-200 text-sm uppercase tracking-wider cursor-pointer disabled:bg-stone-600 disabled:cursor-not-allowed"
          >
            {status.submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Sender...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span>Send besked</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  )
}
